import { TextField } from "@mui/material";
import { useCallback } from "react";
import { isRelativeDateCondition } from "../../segment-operation/operators";
import { AutocompleteCountrySelect } from "./value-select/autocomplete-country-select";
import { AutocompleteValueSelect } from "./value-select/autocomplete-select";
import { DateSelectField } from "./value-select/date-select";
import { MonthDaySelectField } from "./value-select/month-day-select";

import type { UsableAccountPersonField } from "../../../server/supabase-segment-field-service";
import type { Condition } from "../../../types/segment-schema";
import type { FC } from "react";

export const SegmentValueField: FC<{
  condition: Condition;
  handleFieldChange: (cond: Partial<Condition>) => void;
  errMsg?: string;
  disabled?: boolean;
  fieldType?: UsableAccountPersonField["field_type"];
  segmentField?: UsableAccountPersonField;
  recipientTypeKey?: string;
}> = ({
  condition,
  handleFieldChange: baseHandleFieldChange,
  errMsg: error,
  disabled,
  fieldType,
  segmentField,
  recipientTypeKey,
}) => {
  const hidden = condition.operation === "IS EMPTY";

  const handleFieldChange = useCallback(
    (values: string[]) => baseHandleFieldChange({ values }),
    [baseHandleFieldChange],
  );

  switch (fieldType) {
    case undefined:
    case null:
      return (
        <TextField
          label="Value"
          hidden={hidden}
          disabled={true}
          variant="outlined"
          fullWidth
          data-testid="value-autocomplete"
        />
      );
    case "BOOLEAN":
      return null;
    case "TEXT":
      return (
        <AutocompleteValueSelect
          condition={condition}
          handleFieldChange={handleFieldChange}
          errMsg={error}
          segmentField={segmentField}
          disabled={disabled}
          hidden={hidden}
          recipientTypeKey={recipientTypeKey}
          sx={{ width: "100%" }}
          data-testid="value-autocomplete"
        />
      );
    case "MONTH_DAY":
      return (
        <MonthDaySelectField
          condition={condition}
          handleFieldChange={handleFieldChange}
          disabled={disabled}
          hidden={hidden}
          data-testid="value-autocomplete"
        />
      );

    case "DATE":
      return (
        <DateSelectField
          condition={condition}
          handleFieldChange={handleFieldChange}
          disabled={disabled}
          hidden={hidden || isRelativeDateCondition(condition)}
          error={error}
          segmentField={segmentField}
          sx={{ width: "100%" }}
          data-testid="value-autocomplete"
        />
      );

    case "COUNTRY":
      return (
        <AutocompleteCountrySelect
          condition={condition}
          handleFieldChange={handleFieldChange}
          errMsg={error}
          segmentField={segmentField}
          disabled={disabled}
          hidden={hidden}
          sx={{ width: "100%" }}
          data-testid="value-autocomplete"
        />
      );
    case "ADDRESS":
      return (
        <TextField
          hidden={hidden}
          disabled={true}
          placeholder="Value"
          variant="outlined"
          sx={{ width: "100%" }}
          data-testid="value-autocomplete"
        ></TextField>
      );
  }
};
